/**
 * v2 VdslPortsCard — Huawei VDSL inventory grid (clipboard + info-icon
 * expand pattern, same as the PON variant). No row-click action; the
 * info-icon expands an inline detail panel.
 *
 * Reads ``GET /api/chassis/{id}/vdsl-ports`` (Phase 5 endpoint).
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../../api/client";
import { LocalTime } from "../../../../lib/datetime";
import { Badge } from "../../../components/Badge";
import { CopyableCell } from "../../../components/CopyableCell";
import { InfoExpandButton } from "../../../components/InfoExpandButton";
import { OccupancyIcon } from "../../../components/OccupancyIcon";
import { RefreshLineButton } from "../../../components/RefreshLineButton";
import { VdslErrorChip } from "../../../components/VdslErrorChip";
import {
  isVdslErrorFlagged,
  VDSL_STATE_TONE,
  type VdslErrors7d,
} from "../../../lib/signalTones";

type VdslPortRow = {
  chassis_id: number;
  frame: number;
  slot: number;
  port: number;
  line_state: string | null;
  loopback: string | null;
  template_index: number | null;
  last_seen_mac: string | null;
  last_seen_device: string | null;
  occupied: boolean | null;
  up_act_rate_kbps: number | null;
  dw_act_rate_kbps: number | null;
  up_snr_margin_db: number | null;
  dw_snr_margin_db: number | null;
  last_sample_at: string | null;
  last_selt_termination: string | null;
  last_selt_at: string | null;
  missing_scans: number;
  observed_at: string;
  // Line error-health flag (7-day ES/SES/UAS/FEC/retrains, worker-computed).
  error_health: string | null;
  errors_7d: VdslErrors7d | null;
  error_health_at: string | null;
};

type VdslPortListResponse = {
  chassis_id: number;
  ports: VdslPortRow[];
};

function fmtRate(kbps: number | null): string {
  if (kbps == null) return "—";
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
  return `${kbps} kbps`;
}

export function VdslPortsCard({ chassisId }: { chassisId: string }) {
  const q = useQuery<VdslPortListResponse>({
    queryKey: ["v2", "chassis", chassisId, "vdsl-ports"],
    queryFn: async () => {
      const res = await api.get<VdslPortListResponse>(
        `/chassis/${chassisId}/vdsl-ports`,
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set());
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const allPorts = q.data?.ports ?? [];
  const flagged = allPorts.filter((p) => isVdslErrorFlagged(p.error_health));
  const flaggedCritical = flagged.filter(
    (p) => p.error_health === "critical",
  ).length;

  // Submit a single-line VDSL refresh (refresh_port) and hand off to the
  // InlineJobTracker. The grid row updates itself when the job's terminal
  // chassis_inventory_updated / chassis_macs_updated events invalidate the
  // vdsl-ports query (see useEventStream).
  const refreshOne = async (p: VdslPortRow) => {
    const k = `${p.slot}-${p.port}`;
    setRefreshing((s) => new Set(s).add(k));
    try {
      const res = await api.post<{ id: string }>(
        `/chassis/${chassisId}/jobs`,
        {
          name: "refresh_port",
          params: { frame: p.frame, slot: p.slot, port: p.port },
        },
      );
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: res.data.id, kind: "refresh_port" },
        }),
      );
    } finally {
      setRefreshing((s) => {
        const n = new Set(s);
        n.delete(k);
        return n;
      });
    }
  };

  // Group by slot.
  const bySlot = ((): { slot: number; ports: VdslPortRow[] }[] => {
    const m = new Map<number, VdslPortRow[]>();
    for (const p of flaggedOnly && flagged.length > 0 ? flagged : allPorts) {
      const key = p.slot;
      const arr = m.get(key);
      if (arr) arr.push(p);
      else m.set(key, [p]);
    }
    return Array.from(m.entries())
      .map(([slot, ports]) => ({
        slot,
        ports: ports.sort((a, b) => a.port - b.port),
      }))
      .sort((a, b) => a.slot - b.slot);
  })();

  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          VDSL ports
        </h2>
        <span className="flex items-center gap-2 text-[11px] text-slate-500">
          {flagged.length > 0 && (
            <button
              type="button"
              onClick={() => setFlaggedOnly((v) => !v)}
              aria-pressed={flaggedOnly}
              title={
                flaggedOnly
                  ? "Showing only ports flagged for line errors. Click to show all."
                  : "Ports whose last 7 days of ES / SES / UAS / FEC / retrains crossed a threshold. Click to show only these."
              }
              className={[
                "rounded border px-1.5 py-0.5",
                flaggedCritical > 0
                  ? "border-red-700 bg-red-900/40 text-red-200"
                  : "border-amber-700 bg-amber-900/40 text-amber-200",
                flaggedOnly ? "ring-1 ring-slate-300" : "",
              ].join(" ")}
            >
              ⚠ {flagged.length} flagged{flaggedOnly ? " · showing only" : ""}
            </button>
          )}
          <span>
            {bySlot.length} board{bySlot.length === 1 ? "" : "s"} ·{" "}
            {allPorts.length} ports
          </span>
        </span>
      </div>
      {q.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : bySlot.length === 0 ? (
        <p className="text-xs text-slate-500">
          No ports in inventory. Run <em>List ports</em> from the
          Run-a-job dropdown above.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {bySlot.map(({ slot, ports }) => (
            <details
              key={slot}
              open
              className="rounded border border-slate-800 bg-slate-950/40"
            >
              <summary className="cursor-pointer px-3 py-1.5 text-xs uppercase tracking-wide text-slate-400">
                Slot {slot} · {ports.length} port
                {ports.length === 1 ? "" : "s"}
              </summary>
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="w-12 px-2 py-1"></th>
                    <th className="px-2 py-1">Port</th>
                    <th className="px-2 py-1">State</th>
                    <th className="px-2 py-1">Up / Dw</th>
                    <th className="px-2 py-1">SNR (u/d)</th>
                    <th className="px-2 py-1">Template</th>
                    <th className="px-2 py-1">Last MAC</th>
                    <th className="px-2 py-1">Device</th>
                    <th className="px-2 py-1">SELT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40 text-slate-300">
                  {ports.map((p) => {
                    const key = `${p.slot}-${p.port}`;
                    const isExpanded = expanded.has(key);
                    return (
                      <>
                        <tr key={`row-${key}`}>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center justify-center gap-1">
                              <InfoExpandButton
                                expanded={isExpanded}
                                label={`Show detail for port ${p.frame}/${p.slot}/${p.port}`}
                                onClick={() =>
                                  setExpanded((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(key)) next.delete(key);
                                    else next.add(key);
                                    return next;
                                  })
                                }
                              />
                              <RefreshLineButton
                                busy={refreshing.has(key)}
                                label={`Refresh all stats for port ${p.frame}/${p.slot}/${p.port}`}
                                onClick={() => refreshOne(p)}
                              />
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <CopyableCell
                              value={`${p.frame}/${p.slot}/${p.port}`}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="inline-flex items-center gap-1">
                              <Badge tone={VDSL_STATE_TONE[p.line_state ?? ""] ?? "neutral"}>
                                {p.line_state ?? "?"}
                              </Badge>
                              <VdslErrorChip
                                health={p.error_health}
                                errors={p.errors_7d}
                              />
                            </span>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">
                            {fmtRate(p.up_act_rate_kbps)} /{" "}
                            {fmtRate(p.dw_act_rate_kbps)}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[11px] text-slate-400">
                            {p.up_snr_margin_db?.toFixed(1) ?? "—"} /{" "}
                            {p.dw_snr_margin_db?.toFixed(1) ?? "—"}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[11px] text-slate-400">
                            {p.template_index ?? "—"}
                          </td>
                          <td className="px-2 py-1.5">
                            <CopyableCell value={p.last_seen_mac} />
                          </td>
                          <td className="px-2 py-1.5 text-[11px]">
                            {p.last_seen_device ? (
                              <span className="inline-flex items-center gap-1">
                                <OccupancyIcon occupied={p.occupied} />
                                <CopyableCell value={p.last_seen_device} />
                              </span>
                            ) : (
                              <span className="text-slate-600">—</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            {p.last_selt_termination ? (
                              <Badge
                                tone={
                                  p.last_selt_termination === "open"
                                    ? "success"
                                    : "danger"
                                }
                              >
                                {p.last_selt_termination}
                              </Badge>
                            ) : (
                              <span className="text-slate-600">—</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr
                            key={`exp-${key}`}
                            className="bg-slate-950/60"
                          >
                            <td></td>
                            <td colSpan={8} className="px-3 py-2 text-[11px]">
                              <PortDetail
                                chassisId={chassisId}
                                slot={p.slot}
                                port={p.port}
                                row={p}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}

function PortDetail({
  chassisId,
  slot,
  port,
  row,
}: {
  chassisId: string;
  slot: number;
  port: number;
  row: VdslPortRow;
}) {
  // Lightweight detail summary. Full per-port history (chart,
  // SELT table, error counters, destructive actions) is on the
  // dedicated detail page at /v2/chassis/{id}/vdsl/{slot}/{port}.
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <h4 className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
          Latest sample
        </h4>
        <dl className="grid grid-cols-2 gap-1 text-slate-400">
          <dt className="text-slate-500">Up actual</dt>
          <dd className="font-mono">{fmtRate(row.up_act_rate_kbps)}</dd>
          <dt className="text-slate-500">Dw actual</dt>
          <dd className="font-mono">{fmtRate(row.dw_act_rate_kbps)}</dd>
          <dt className="text-slate-500">SNR up</dt>
          <dd className="font-mono">{row.up_snr_margin_db?.toFixed(1) ?? "—"}</dd>
          <dt className="text-slate-500">SNR dw</dt>
          <dd className="font-mono">{row.dw_snr_margin_db?.toFixed(1) ?? "—"}</dd>
          <dt className="text-slate-500">Sampled</dt>
          <dd className="font-mono">
            <LocalTime iso={row.last_sample_at} />
          </dd>
        </dl>
      </div>
      <div>
        <h4 className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
          SELT
        </h4>
        {row.last_selt_at ? (
          <p className="text-slate-400">
            <LocalTime iso={row.last_selt_at} /> ·{" "}
            <Badge tone={row.last_selt_termination === "open" ? "success" : "danger"}>
              {row.last_selt_termination}
            </Badge>
          </p>
        ) : (
          <p className="text-slate-500">No SELT run yet.</p>
        )}
        <p className="mt-2 text-[10px] text-slate-500">
          <a
            href={`/chassis/${chassisId}/vdsl/${slot}/${port}`}
            className="text-emerald-400 hover:text-emerald-300"
          >
            Open full detail page →
          </a>
        </p>
      </div>
    </div>
  );
}
